import { PixelCrop } from 'react-image-crop';

// The code here is originally from:
//   https://codesandbox.io/s/react-image-crop-demo-with-react-hooks-y831o?file=/src/canvasPreview.ts
//   https://stackoverflow.com/questions/69546480/how-to-crop-and-zoom-an-image-with-react-image-crop
// It has been adapted to support a maximum resolution
// What was originally called `scale` is called `zoom` here

const TO_RADIANS = Math.PI / 180;
const DEFAULT_MAX_RESOLUTION = 8_000_000; // 8 megapixels

interface CropAndResizeOptions {
  crop: PixelCrop;
  zoom?: number;
  rotate?: number;
  maxResolution?: number;
  useDevicePixelRatio?: boolean;
}

const createCroppedImage = (
  image: HTMLImageElement,
  {
    crop,
    zoom = 1,
    rotate = 0,
    maxResolution = DEFAULT_MAX_RESOLUTION,
    useDevicePixelRatio = false,
  }: CropAndResizeOptions,
) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // devicePixelRatio slightly increases sharpness on retina devices
  // at the expense of slightly slower render times and needing to
  // size the image back down if you want to download/upload and be
  // true to the images natural size.
  const pixelRatio = useDevicePixelRatio ? window.devicePixelRatio : 1;

  // Must keep track of the delta between the displayed size
  //   which the crop dimensions are based on (image.width)
  // And the actual image dimensions (image.naturalWidth)
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  // We first calculate what the resolution of the cropped image would be
  //   given the original file, pixel ratio and crop
  const croppedWidth = Math.floor(crop.width * scaleX * pixelRatio);
  const croppedHeight = Math.floor(crop.height * scaleY * pixelRatio);
  const croppedResolution = croppedWidth * croppedHeight;

  // This is equivalent to `resolutionRatio = croppedResolution > maxResolution ? 1 : croppedResolution`
  const resolutionRatio = Math.min(
    1,
    Math.sqrt(maxResolution / croppedResolution),
  );

  // If we need to resize, we define by which factor here.
  const resizeRatio = pixelRatio * resolutionRatio;

  canvas.width = Math.floor(crop.width * scaleX * resizeRatio);
  canvas.height = Math.floor(crop.height * scaleY * resizeRatio);

  ctx.scale(resizeRatio, resizeRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = rotate * TO_RADIANS;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();

  // 5) Move the crop origin to the canvas origin (0,0)
  ctx.translate(-cropX, -cropY);
  // 4) Move the origin to the center of the original position
  ctx.translate(centerX, centerY);
  // 3) Rotate around the origin
  ctx.rotate(rotateRads);
  // 2) Scale the image
  ctx.scale(zoom, zoom);
  // 1) Move the center of the image to the origin (0,0)
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
  );

  ctx.restore();

  // Now we can return the image blob
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) =>
      b ? resolve(b) : reject(new Error('Could not produce cropped image')),
    ),
  );
};

export default createCroppedImage;
